import { useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { LenisContext } from "@/lenis-context";
import Logo from "@/components/Logo";

export const FLAGS = {
  fr: { src: "https://flagcdn.com/w40/fr.png", label: "Français" },
  en: { src: "https://flagcdn.com/w40/gb.png", label: "English" },
  es: { src: "https://flagcdn.com/w40/es.png", label: "Español" },
  de: { src: "https://flagcdn.com/w40/de.png", label: "Deutsch" },
  it: { src: "https://flagcdn.com/w40/it.png", label: "Italiano" },
  nl: { src: "https://flagcdn.com/w40/nl.png", label: "Nederlands" },
  ru: { src: "https://flagcdn.com/w40/ru.png", label: "Русский" },
  uk: { src: "https://flagcdn.com/w40/ua.png", label: "Українська" },
};

export default function Header() {
  const { lang, setLang, t } = useLang();
  const scrollTo = useContext(LenisContext);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const close = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const links = [
    ["#accueil", t.nav.home, "home"],
    ["#histoire", t.nav.story, "story"],
    ["#equipe", t.nav.team, "team"],
    ["#etablissements", t.nav.establishments, "establishments"],
    ["#presse", t.nav.press, "press"],
    ["#contact", t.nav.contact, "contact"],
  ];

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollTo?.(href);
  };

  const active = FLAGS[lang];

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/40 bg-paper/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-10">
        <a
          href="#accueil"
          data-testid="header-logo"
          onClick={(e) => go(e, "#accueil")}
          className="transition-opacity duration-300 hover:opacity-70"
        >
          <Logo />
        </a>

        <nav className="hidden items-center gap-7 xl:flex" aria-label="Navigation principale">
          {links.map(([href, label, id]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-${id}`}
              onClick={(e) => go(e, href)}
              className="group relative text-sm font-medium tracking-wide text-pine/80 transition-colors duration-300 hover:text-pine"
            >
              {label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-wood transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative" ref={langRef}>
            <button
              type="button"
              data-testid="lang-selector-toggle"
              onClick={() => setLangOpen((v) => !v)}
              aria-expanded={langOpen}
              aria-haspopup="listbox"
              aria-label={`Langue : ${active.label}`}
              className="flex items-center gap-2 rounded-full border border-pine/15 bg-paper/60 py-1.5 pl-2 pr-3 transition-all duration-300 hover:border-pine/35 hover:bg-paper"
            >
              <span className="h-4 w-6 overflow-hidden rounded-[3px]">
                <img src={active.src} alt="" className="h-full w-full object-cover" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-pine">
                {lang}
              </span>
              <ChevronDown
                size={14}
                className={`text-pine/60 transition-transform duration-300 ${langOpen ? "rotate-180" : ""}`}
              />
            </button>

            <AnimatePresence>
              {langOpen && (
                <motion.ul
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  role="listbox"
                  aria-label="Choisir la langue"
                  data-testid="lang-selector-menu"
                  className="absolute right-0 top-full z-50 mt-2 w-48 origin-top-right overflow-hidden rounded-xl border border-pine/10 bg-paper/95 p-1.5 shadow-xl backdrop-blur-xl"
                >
                  {Object.entries(FLAGS).map(([code, flag]) => (
                    <li key={code}>
                      <button
                        type="button"
                        data-testid={`lang-switch-${code}`}
                        role="option"
                        aria-selected={lang === code}
                        onClick={() => {
                          setLang(code);
                          setLangOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors duration-200 ${
                          lang === code
                            ? "bg-pine/5 font-semibold text-pine"
                            : "text-pine/75 hover:bg-pine/5 hover:text-pine"
                        }`}
                      >
                        <span className="h-4 w-6 shrink-0 overflow-hidden rounded-[3px]">
                          <img src={flag.src} alt="" className="h-full w-full object-cover" />
                        </span>
                        <span className="flex-1">{flag.label}</span>
                        {lang === code && <Check size={14} className="text-wood" />}
                      </button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <button
            type="button"
            data-testid="menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="rounded-full p-2 text-pine transition-colors hover:bg-pine/5 xl:hidden"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-pine/10 bg-paper/95 backdrop-blur-xl xl:hidden"
            aria-label="Navigation mobile"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map(([href, label, id]) => (
                <a
                  key={href}
                  href={href}
                  data-testid={`nav-mobile-${id}`}
                  onClick={(e) => go(e, href)}
                  className="border-b border-pine/5 py-4 font-serif text-2xl text-pine transition-colors last:border-0 hover:text-wood"
                >
                  {label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
